import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';
import { BackgroundJobDto, BackgroundJobType, BackgroundJobTypeMetadataDto } from '@travel-planner/library-travel-planner-api';
import { BackgroundJobsActions } from './background-jobs.actions';

export interface State extends EntityState<BackgroundJobDto> {
    loading: boolean;
    pollingJobIds: string[];
    total: number | null;
    jobTypes: BackgroundJobTypeMetadataDto[];
}

export const adapter: EntityAdapter<BackgroundJobDto> = createEntityAdapter<BackgroundJobDto>({
    selectId: (job) => job.id!,
});

export const initialState: State = adapter.getInitialState({
    loading: false,
    pollingJobIds: [],
    total: null,
    jobTypes: [],
});

export const backgroundJobsFeature = createFeature({
    name: 'backgroundJobs',
    reducer: createReducer(
        initialState,
        on(BackgroundJobsActions.loadBackgroundJobs, (state, { skipLoadingIndicator }) => ({
            ...state,
            loading: skipLoadingIndicator ? state.loading : true
        })),

        on(BackgroundJobsActions.loadBackgroundJobsSuccess, (state, { result }) => ({
            ...adapter.setAll(result.backgroundJobs ?? [], state),
            total: result.count ?? null,
            loading: false
        })),

        on(BackgroundJobsActions.loadBackgroundJobsFailure, (state) => ({
            ...state,
            loading: false
        })),

        on(BackgroundJobsActions.loadBackgroundJobTypesSuccess, (state, { jobTypes }) => ({
            ...state,
            jobTypes
        })),

        on(BackgroundJobsActions.registerJob, (state, { job, relatedEntityId }) =>
            adapter.upsertOne(
                {
                    id: job.id,
                    type: job.type,
                    displayName: job.displayName,
                    description: job.description,
                    status: job.status,
                    createdAt: job.createdAt,
                    relatedEntityId: relatedEntityId ?? undefined
                } as BackgroundJobDto,
                state
            )
        ),

        on(BackgroundJobsActions.jobPollingStarted, (state, { jobId }) => ({
            ...state,
            pollingJobIds: state.pollingJobIds.includes(jobId)
                ? state.pollingJobIds
                : [...state.pollingJobIds, jobId]
        })),

        on(BackgroundJobsActions.jobStatusUpdated, (state, { job }) =>
            adapter.upsertOne(job, state)
        ),

        on(BackgroundJobsActions.jobCompleted, (state, { job }) => ({
            ...adapter.upsertOne(job, state),
            pollingJobIds: state.pollingJobIds.filter(id => id !== job.id)
        })),

        on(BackgroundJobsActions.jobFailed, (state, { job }) => ({
            ...adapter.upsertOne(job, state),
            pollingJobIds: state.pollingJobIds.filter(id => id !== job.id)
        })),

        on(BackgroundJobsActions.jobPollingFailed, (state, { jobId }) => ({
            ...state,
            pollingJobIds: state.pollingJobIds.filter(id => id !== jobId)
        })),

        on(BackgroundJobsActions.jobPollingTimedOut, (state, { jobId }) => ({
            ...state,
            pollingJobIds: state.pollingJobIds.filter(id => id !== jobId)
        })),
    ),
});

const { selectAll, selectEntities } = adapter.getSelectors();

export const selectBackgroundJobsState = backgroundJobsFeature.selectBackgroundJobsState;

export const selectAllBackgroundJobs = createSelector(
    selectBackgroundJobsState,
    selectAll
);

export const selectBackgroundJobEntities = createSelector(
    selectBackgroundJobsState,
    selectEntities
);

export const selectBackgroundJobById = (id: string) => createSelector(
    selectBackgroundJobEntities,
    (entities) => entities[id]
);

export const selectBackgroundJobByTypeAndEntityId = (
    type: BackgroundJobType,
    entityId: number | null | undefined
) => createSelector(
    selectAllBackgroundJobs,
    (jobs) => {
        if (!entityId) {
            return undefined;
        }

        return jobs
            .filter(job => job.type === type && job.relatedEntityId === entityId)
            .sort((a, b) => new Date(b.createdAt ?? '').getTime() - new Date(a.createdAt ?? '').getTime())[0];
    }
);

export const selectPollingJobIds = createSelector(
    selectBackgroundJobsState,
    state => state.pollingJobIds
);

export const selectBackgroundJobsTotal = createSelector(
    selectBackgroundJobsState,
    state => state.total
);

export const selectBackgroundJobTypes = createSelector(
    selectBackgroundJobsState,
    state => state.jobTypes
);

export const selectManualBackgroundJobTypes = createSelector(
    selectBackgroundJobTypes,
    jobTypes => jobTypes.filter(jobType => jobType.canStartManually)
);

export const selectBackgroundJobsLoading = createSelector(
    selectBackgroundJobsState,
    state => state.loading
);

export const selectIsJobPolling = (jobId: string) => createSelector(
    selectPollingJobIds,
    pollingJobIds => pollingJobIds.includes(jobId)
);
